import api from 'api/apiClient';

type FileCategory = 'images' | 'videos' | 'documents' | 'unknown';

function detectFileType(file: File): FileCategory {
  if (!file) return 'documents';
  if (file.type?.startsWith('image/')) return 'images';
  if (file.type?.startsWith('video/')) return 'videos';
  const ext = file.name.split('.').pop()?.toLowerCase();
  const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
  if (ext && docExts.includes(ext)) return 'documents';
  return 'unknown';
}

export interface UploadedFile {
  id: string | number | undefined;
  url: string | undefined;
  name?: string;
  fileType?: string;
}

export async function uploadFile(file: File): Promise<UploadedFile> {
  const fileType = detectFileType(file);
  if (fileType === 'unknown') throw new Error('Unsupported file type');

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    const res = await api.post('/api/file/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // FileController returns data: { id, name, url, file_type } — reading `file_id`
    // here silently produced an undefined id for every upload.
    const uploaded = res.data?.data ?? {};

    return {
      id: uploaded.id,
      url: uploaded.url,
      name: uploaded.name,
      fileType: uploaded.file_type,
    };
  } catch (err) {
    throw new Error((err as any)?.userMessage || 'File upload failed');
  }
}

export type FileWrapper = number | { id?: number | string; file?: File } | null | undefined;

export async function ensureFileId(wrapper: FileWrapper): Promise<number | string | null> {
  if (!wrapper) return null;
  if (typeof wrapper === 'number') return wrapper;
  if ((wrapper as any).id) return (wrapper as any).id;
  if ((wrapper as any).file instanceof File) {
    const uploaded = await uploadFile((wrapper as any).file);
    return uploaded.id ?? null;
  }
  return null;
}

export async function ensureFileArray(arr: FileWrapper[] = []): Promise<(number | string | null)[]> {
  return Promise.all(arr.map(ensureFileId));
}
